import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  StarIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon,
  HeartIcon as HeartIconSolid,
} from "@heroicons/react/24/solid";
import {
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Button,
  Card,
  Center,
  Chip,
  Container,
  Grid,
  Group,
  Loader,
  Modal,
  Paper,
  Rating,
  rem,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useCallback, useEffect, useState } from "react";
import { FeatureUnavailableBanner } from "../components/common/FeatureUnavailableBanner";
import { Header } from "../components/layout/Header";
import { ROUTES } from "../constants/routes";
import { useCurrentUser } from "../hooks/queries/useCurrentUser";
import { useFeatureFlags } from "../hooks/queries/useFeatureFlags";
import { useTechnologiesDictionary } from "../hooks/queries/useTechnologiesDictionary";
import {
  CreateSkillAssessmentRequest,
  SkillMatrixEntry,
} from "../models/skill-matrix.model";
import { PersonTechnologyCreateRequest } from "../models/skill.model";
import { TechnologyExperience } from "../models/technologyExperience.model";
import { ChatService } from "../services/chatService";
import { PersonTechnologyService } from "../services/personTechnologyService";
import { ProjectService } from "../services/projectService";
import { SkillMatrixService } from "../services/skillMatrixService";
import { TechnologyService } from "../services/technologyService";

export function MySkills() {
  const [skills, setSkills] = useState<SkillMatrixEntry[]>([]);
  const [filteredSkills, setFilteredSkills] = useState<SkillMatrixEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 1200); // 1.2 seconds debounce for better user experience
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [personId, setPersonId] = useState<number | null>(null);
  // availableTechnologies now comes from React Query hook

  const [noSkillsFound, setNoSkillsFound] = useState(false);
  const [similarSkills, setSimilarSkills] = useState<string[]>([]);
  const [isLoadingSimilarSkills, setIsLoadingSimilarSkills] = useState(false);
  const [sortBy, setSortBy] = useState<
    "technology" | "category" | "skillLevel" | "yearsExperience" | "status"
  >("technology");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Add skill form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormTechnology, setAddFormTechnology] = useState<string | null>(
    null
  );
  const [addFormSkillLevel, setAddFormSkillLevel] = useState<number>(1);
  const [addFormYearsExperience, setAddFormYearsExperience] = useState<
    number | null
  >(null);
  const [addFormYearsInput, setAddFormYearsInput] = useState("");
  const [addFormIsFavorite, setAddFormIsFavorite] = useState(false);
  const [addFormComments, setAddFormComments] = useState("");
  const [isAddingSkill, setIsAddingSkill] = useState(false);

  // Assessment form state
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [assessmentSkill, setAssessmentSkill] =
    useState<SkillMatrixEntry | null>(null);
  const [assessmentFormSkillLevel, setAssessmentFormSkillLevel] =
    useState<number>(1);
  const [assessmentFormYearsExperience, setAssessmentFormYearsExperience] =
    useState<number | null>(null);
  const [assessmentFormYearsInput, setAssessmentFormYearsInput] = useState("");
  const [assessmentFormComments, setAssessmentFormComments] = useState("");
  const [isCreatingAssessment, setIsCreatingAssessment] = useState(false);

  // Bulk edit mode state
  const [isBulkEditMode, setIsBulkEditMode] = useState(false);
  const [bulkEditChanges, setBulkEditChanges] = useState<
    Map<number, Partial<SkillMatrixEntry>>
  >(new Map());
  const [isSavingBulkChanges, setIsSavingBulkChanges] = useState(false);
  const [localYearsInputs, setLocalYearsInputs] = useState<Map<number, string>>(
    new Map()
  );

  // Technology experience from projects
  const [technologyExperience, setTechnologyExperience] = useState<TechnologyExperience[]>([]);

  const technologyService = new TechnologyService();
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const { data: featureFlags } = useFeatureFlags();
  const skillAssignmentEnabled = featureFlags?.enableSkillAssignment ?? true;
  const { data: availableTechnologies, isLoading: technologiesLoading } =
    useTechnologiesDictionary();
  const personTechnologyService = new PersonTechnologyService();
  const skillMatrixService = new SkillMatrixService();
  const projectService = new ProjectService();
  const chatService = ChatService.getInstance();

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    setNoSkillsFound(false);
    try {
      // Use current user from React Query
      if (!currentUser) {
        throw new Error("User not loaded");
      }

      setPersonId(currentUser.personId);

      // If personId is null, treat as no skills case
      if (currentUser.personId === null) {
        setNoSkillsFound(true);
        setSkills([]);
        setFilteredSkills([]);
        setTechnologyExperience([]);
      } else {
        try {
          // Get user's skills and technology experience in parallel
          const [userSkills, techExperience] = await Promise.all([
            skillMatrixService.getSkillMatrixByPerson(currentUser.personId),
            projectService.getTechnologyExperienceByPerson(currentUser.personId).catch((err) => {
              console.error("Error fetching technology experience:", err);
              return []; // Return empty array if technology experience fails
            }),
          ]);
          setSkills(userSkills);
          setFilteredSkills(userSkills);
          setTechnologyExperience(techExperience);
        } catch (skillErr) {
          // Handle 404 error specifically (no skills found)
          if (skillErr instanceof Error && skillErr.message.includes("404")) {
            setNoSkillsFound(true);
            setSkills([]);
            setFilteredSkills([]);
            setTechnologyExperience([]);
          } else {
            // Re-throw other errors to be caught by the outer catch
            throw skillErr;
          }
        }
      }

      // availableTechnologies now comes from React Query hook - no need to fetch manually
    } catch (err) {
      console.error("Error loading skills data:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load data. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      currentUser &&
      !userLoading &&
      availableTechnologies &&
      !technologiesLoading
    ) {
      loadData();
    }
  }, [currentUser, userLoading, availableTechnologies, technologiesLoading]);

  const sortSkills = useCallback(() => {
    if (!skills.length) return;

    const filtered = skills.filter((skill) => {
      const matchesSearch = skill.technologyName
        .toLowerCase()
        .includes(debouncedSearchQuery.toLowerCase());
      const matchesCategory = !selectedCategory || selectedCategory === "all";

      return matchesSearch && matchesCategory;
    });

    const sortedSkills = [...filtered];
    sortedSkills.sort((a, b) => {
      let comparison = 0;

      if (sortBy === "technology") {
        comparison = a.technologyName.localeCompare(b.technologyName);
      } else if (sortBy === "category") {
        // Since we don't have categories in the new model, sort by technology name
        comparison = a.technologyName.localeCompare(b.technologyName);
      } else if (sortBy === "skillLevel") {
        comparison = (a.levelSelfRating || 0) - (b.levelSelfRating || 0);
      } else if (sortBy === "yearsExperience") {
        comparison = (a.yearsExperience || 0) - (b.yearsExperience || 0);
      } else if (sortBy === "status") {
        // Sort by assessment/confirmation status
        const aStatus = a.hasAssessment ? (a.isConfirmed ? 2 : 1) : 0;
        const bStatus = b.hasAssessment ? (b.isConfirmed ? 2 : 1) : 0;
        comparison = aStatus - bStatus;
      }

      return sortDirection === "desc" ? -comparison : comparison;
    });

    setFilteredSkills(sortedSkills);
  }, [skills, debouncedSearchQuery, selectedCategory, sortBy, sortDirection]);

  useEffect(() => {
    sortSkills();
  }, [sortSkills]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.currentTarget.value);
  };

  const handleCategoryChange = (value: string | null) => {
    setSelectedCategory(value);
  };

  const handleAddNewSkill = () => {
    setShowAddForm(true);
    setAddFormTechnology(null);
    setAddFormSkillLevel(1);
    setAddFormYearsExperience(null);
    setAddFormYearsInput("");
    setAddFormIsFavorite(false);
    setAddFormComments("");
    setSimilarSkills([]);
  };

  const handleCancelAddSkill = () => {
    setShowAddForm(false);
    setAddFormTechnology(null);
    setAddFormSkillLevel(1);
    setAddFormYearsExperience(null);
    setAddFormYearsInput("");
    setAddFormIsFavorite(false);
    setAddFormComments("");
    setSimilarSkills([]);
  };

  const handleAddFormYearsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.currentTarget.value;
    setAddFormYearsInput(value);

    if (value.trim() === "") {
      setAddFormYearsExperience(null);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setAddFormYearsExperience(numValue);
      }
    }
  };

  const handleSaveNewSkill = async () => {
    if (!personId || !addFormTechnology || !availableTechnologies) return;

    const technology = availableTechnologies.find(
      (tech) => tech.id.toString() === addFormTechnology
    );
    if (!technology) return;

    // Check if skill already exists
    const existingSkill = skills.find(
      (skill) => skill.technologyId === technology.id
    );
    if (existingSkill) {
      setError(
        `Skill "${technology.name}" already exists. You can create a new assessment for it.`
      );
      setTimeout(() => setError(null), 3000);
      return;
    }

    setIsAddingSkill(true);
    try {
      // First create the PersonTechnology entry
      const newPersonTech: PersonTechnologyCreateRequest = {
        id: 0,
        personId: personId,
        technologyId: technology.id,
        favourite: addFormIsFavorite,
        skillLevel: null, // Not used in v2
        yearsOfExperience: null, // Not used in v2
      };

      const savedPersonTech = await personTechnologyService.addPersonTechnology(
        newPersonTech
      );

      // Then create the skill assessment
      const assessmentRequest: CreateSkillAssessmentRequest = {
        personId: personId,
        personTechnologyId: savedPersonTech.id,
        levelSelfRating: addFormSkillLevel,
        yearsExperience: addFormYearsExperience,
        comments: addFormComments || null,
      };

      await skillMatrixService.createSkillAssessment(assessmentRequest);

      // Reload data to get the updated skill matrix
      await loadData();

      // Reset form
      handleCancelAddSkill();

      // Fetch similar skills after adding a new skill
      try {
        setIsLoadingSimilarSkills(true);
        const similarSkillsResponse = await chatService.suggestSimilarSkills(
          technology.name
        );
        setSimilarSkills(similarSkillsResponse);
      } catch (error) {
        console.error("Error fetching similar skills:", error);
        setSimilarSkills([]);
      } finally {
        setIsLoadingSimilarSkills(false);
      }
    } catch (err) {
      console.error("Error saving skill:", err);
      const backendMessage = (err as { responseBody?: { error?: string } })?.responseBody?.error;
      setError(
        backendMessage
        ?? (err instanceof Error ? err.message : "Failed to save skill. Please try again.")
      );
    } finally {
      setIsAddingSkill(false);
    }
  };

  const handleCreateAssessment = (skill: SkillMatrixEntry) => {
    setAssessmentSkill(skill);
    setAssessmentFormSkillLevel(skill.levelSelfRating || 1);
    setAssessmentFormYearsExperience(skill.yearsExperience);
    setAssessmentFormYearsInput(skill.yearsExperience?.toString() || "");
    setAssessmentFormComments(skill.assessmentComments || "");
    setShowAssessmentModal(true);
  };

  const handleCancelAssessment = () => {
    setShowAssessmentModal(false);
    setAssessmentSkill(null);
    setAssessmentFormSkillLevel(1);
    setAssessmentFormYearsExperience(null);
    setAssessmentFormYearsInput("");
    setAssessmentFormComments("");
  };

  const handleAssessmentFormYearsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.currentTarget.value;
    setAssessmentFormYearsInput(value);

    if (value.trim() === "") {
      setAssessmentFormYearsExperience(null);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setAssessmentFormYearsExperience(numValue);
      }
    }
  };

  const handleSaveAssessment = async () => {
    if (!personId || !assessmentSkill) return;

    setIsCreatingAssessment(true);
    try {
      const assessmentRequest: CreateSkillAssessmentRequest = {
        personId: personId,
        personTechnologyId: assessmentSkill.personTechnologyId,
        levelSelfRating: assessmentFormSkillLevel,
        yearsExperience: assessmentFormYearsExperience,
        comments: assessmentFormComments || null,
      };

      await skillMatrixService.createSkillAssessment(assessmentRequest);

      // Reload data to get the updated skill matrix
      await loadData();

      // Close modal
      handleCancelAssessment();
    } catch (err) {
      console.error("Error creating assessment:", err);
      const backendMessage = (err as { responseBody?: { error?: string } })?.responseBody?.error;
      setError(
        backendMessage
        ?? (err instanceof Error ? err.message : "Failed to create assessment. Please try again.")
      );
    } finally {
      setIsCreatingAssessment(false);
    }
  };

  const handleToggleFavorite = async (skill: SkillMatrixEntry) => {
    if (!personId) return;

    if (isBulkEditMode) {
      // In bulk edit mode, just track the change
      const currentChanges = new Map(bulkEditChanges);
      const existingChanges =
        currentChanges.get(skill.personTechnologyId) || {};
      const currentFavorite =
        existingChanges.favourite !== undefined
          ? existingChanges.favourite
          : skill.favourite;

      currentChanges.set(skill.personTechnologyId, {
        ...existingChanges,
        favourite: !currentFavorite,
      });
      setBulkEditChanges(currentChanges);

      // Update local display
      setSkills((prev) =>
        prev.map((s) =>
          s.personTechnologyId === skill.personTechnologyId
            ? { ...s, favourite: !currentFavorite }
            : s
        )
      );
      return;
    }

    try {
      // Update favorite status using the old PersonTechnology API
      // This is still handled by the PersonTechnology endpoint
      const updateRequest = {
        id: skill.personTechnologyId,
        technologyId: skill.technologyId,
        technology: {
          id: skill.technologyId,
          name: skill.technologyName,
          categories: [],
        },
        personId: skill.personId,
        favourite: !skill.favourite,
        skillLevel: null,
        yearsOfExperience: null,
        projects: [],
        workExperiences: [],
      };

      await personTechnologyService.updateFavourite(updateRequest);

      // Update local state
      setSkills((prev) =>
        prev.map((s) =>
          s.personTechnologyId === skill.personTechnologyId
            ? { ...s, favourite: !s.favourite }
            : s
        )
      );
    } catch (err) {
      console.error("Error updating favorite:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update favorite. Please try again."
      );
    }
  };

  const handleBulkSkillLevelChange = (
    skill: SkillMatrixEntry,
    newLevel: number
  ) => {
    if (!isBulkEditMode) return;

    const currentChanges = new Map(bulkEditChanges);
    const existingChanges = currentChanges.get(skill.personTechnologyId) || {};

    currentChanges.set(skill.personTechnologyId, {
      ...existingChanges,
      levelSelfRating: newLevel,
    });
    setBulkEditChanges(currentChanges);

    // Update local display
    setSkills((prev) =>
      prev.map((s) =>
        s.personTechnologyId === skill.personTechnologyId
          ? { ...s, levelSelfRating: newLevel }
          : s
      )
    );
  };

  const handleBulkYearsInputChange = (
    skill: SkillMatrixEntry,
    inputValue: string
  ) => {
    if (!isBulkEditMode) return;

    // Update local input state immediately for responsive UI
    const newInputs = new Map(localYearsInputs);
    newInputs.set(skill.personTechnologyId, inputValue);
    setLocalYearsInputs(newInputs);
  };

  const getYearsInputValue = (skill: SkillMatrixEntry): string => {
    // Use local input value if available, otherwise use skill value
    const localValue = localYearsInputs.get(skill.personTechnologyId);
    if (localValue !== undefined) return localValue;
    return skill.yearsExperience?.toString() || "";
  };

  const handleBulkYearsChange = (
    skill: SkillMatrixEntry,
    newYears: number | null
  ) => {
    if (!isBulkEditMode) return;

    const currentChanges = new Map(bulkEditChanges);
    const existingChanges = currentChanges.get(skill.personTechnologyId) || {};

    currentChanges.set(skill.personTechnologyId, {
      ...existingChanges,
      yearsExperience: newYears,
    });
    setBulkEditChanges(currentChanges);

    // Update local display
    setSkills((prev) =>
      prev.map((s) =>
        s.personTechnologyId === skill.personTechnologyId
          ? { ...s, yearsExperience: newYears }
          : s
      )
    );
  };

  const handleToggleBulkEditMode = () => {
    if (isBulkEditMode) {
      // Exiting bulk edit mode - ask for confirmation if there are unsaved changes
      if (bulkEditChanges.size > 0) {
        if (
          !window.confirm(
            "You have unsaved changes. Are you sure you want to exit bulk edit mode?"
          )
        ) {
          return;
        }
        // Reload data to reset any local changes
        loadData();
        setBulkEditChanges(new Map());
      }
      // Clear local input states
      setLocalYearsInputs(new Map());
    }
    setIsBulkEditMode(!isBulkEditMode);
  };

  const handleSaveBulkChanges = async () => {
    if (!personId || bulkEditChanges.size === 0) return;

    setIsSavingBulkChanges(true);
    try {
      const promises: Promise<any>[] = [];

      for (const [personTechnologyId, changes] of Array.from(
        bulkEditChanges.entries()
      )) {
        const skill = skills.find(
          (s) => s.personTechnologyId === personTechnologyId
        );
        if (!skill) continue;

        // Handle favorite changes
        if (changes.favourite !== undefined) {
          const updateRequest = {
            id: skill.personTechnologyId,
            technologyId: skill.technologyId,
            technology: {
              id: skill.technologyId,
              name: skill.technologyName,
              categories: [],
            },
            personId: skill.personId,
            favourite: changes.favourite,
            skillLevel: null,
            yearsOfExperience: null,
            projects: [],
            workExperiences: [],
          };
          promises.push(personTechnologyService.updateFavourite(updateRequest));
        }

        // Handle skill level or years experience changes
        if (
          changes.levelSelfRating !== undefined ||
          changes.yearsExperience !== undefined
        ) {
          const assessmentRequest: CreateSkillAssessmentRequest = {
            personId: personId,
            personTechnologyId: skill.personTechnologyId,
            levelSelfRating:
              changes.levelSelfRating ?? skill.levelSelfRating ?? 1,
            yearsExperience:
              changes.yearsExperience !== undefined
                ? changes.yearsExperience
                : skill.yearsExperience,
            comments: null,
          };
          promises.push(
            skillMatrixService.createSkillAssessment(assessmentRequest)
          );
        }
      }

      await Promise.all(promises);

      // Clear bulk changes and reload data
      setBulkEditChanges(new Map());
      setLocalYearsInputs(new Map());
      setIsBulkEditMode(false);
      await loadData();
    } catch (err) {
      console.error("Error saving bulk changes:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to save bulk changes. Please try again."
      );
    } finally {
      setIsSavingBulkChanges(false);
    }
  };

  const getStatusBadge = (skill: SkillMatrixEntry) => {
    if (!skill.hasAssessment) {
      return (
        <Badge color="gray" size="sm">
          No Assessment
        </Badge>
      );
    }

    if (skill.isConfirmed) {
      return (
        <Badge
          color="green"
          size="sm"
          leftSection={<CheckCircleIcon style={{ width: rem(12) }} />}
        >
          Confirmed
        </Badge>
      );
    }

    if (skill.needsConfirmation) {
      return (
        <Badge
          color="yellow"
          size="sm"
          leftSection={<ClockIcon style={{ width: rem(12) }} />}
        >
          Pending Review
        </Badge>
      );
    }

    if (skill.isRejected) {
      return (
        <Badge color="red" size="sm">
          Rejected
        </Badge>
      );
    }

    return (
      <Badge color="blue" size="sm">
        Assessed
      </Badge>
    );
  };

  // Get years of experience from technology experience data (calculated from projects)
  const getTechnologyExperience = (technologyName: string): number | null => {
    const experience = technologyExperience.find(
      (exp) => exp.technologyName === technologyName
    );
    return experience ? experience.yearsOfExperience : null;
  };

  const formatYearsOfExperience = (years: number | null): string => {
    if (years === null || years === undefined) return "—";
    if (years === 0) return "< 1 year";
    if (years === 1) return "1 year";
    if (years % 1 === 0) return `${years} years`;
    return `${years.toFixed(1)} years`;
  };

  return (
    <AppShell header={{ height: 60 }} padding="md">
      <AppShell.Header>
        <Header />
      </AppShell.Header>
      <AppShell.Main>
        <Container size="lg" py="xl">
          <Stack gap="md">
            {!skillAssignmentEnabled && (
              <FeatureUnavailableBanner message="Skill assignment is temporarily unavailable — this feature is being rebuilt. Your existing skills are unaffected and still visible below." />
            )}
            <Group justify="space-between">
              <Stack gap="xs">
                <Title order={1}>My Skill Matrix</Title>
                <Text c="dimmed">
                  Manage your technical skills, create assessments, and track
                  confirmation status
                </Text>
              </Stack>
              <Group gap="md">
                {isBulkEditMode ? (
                  <>
                    <Badge color="blue" size="lg">
                      Bulk Edit Mode ({bulkEditChanges.size} changes)
                    </Badge>
                    <Button
                      variant="outline"
                      onClick={handleToggleBulkEditMode}
                      disabled={isSavingBulkChanges}
                    >
                      Cancel
                    </Button>
                    <Button
                      leftSection={<CheckIcon style={{ width: rem(16) }} />}
                      onClick={handleSaveBulkChanges}
                      disabled={bulkEditChanges.size === 0}
                      loading={isSavingBulkChanges}
                      color="green"
                    >
                      Save Changes ({bulkEditChanges.size})
                    </Button>
                  </>
                ) : (
                  <>
                    <Tooltip
                      label="Skill assignment is temporarily unavailable — this feature is being rebuilt."
                      disabled={skillAssignmentEnabled}
                    >
                      <Button
                        variant="outline"
                        leftSection={
                          <PencilSquareIcon style={{ width: rem(18) }} />
                        }
                        onClick={handleToggleBulkEditMode}
                        disabled={skills.length === 0 || !skillAssignmentEnabled}
                      >
                        Bulk Edit
                      </Button>
                    </Tooltip>
                    <Tooltip
                      label="Skill assignment is temporarily unavailable — this feature is being rebuilt."
                      disabled={skillAssignmentEnabled}
                    >
                      <Button
                        leftSection={<PlusIcon style={{ width: rem(18) }} />}
                        onClick={handleAddNewSkill}
                        disabled={!skillAssignmentEnabled}
                      >
                        Add Skill
                      </Button>
                    </Tooltip>
                  </>
                )}
              </Group>
            </Group>

            <Paper p="md" withBorder>
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : error ? (
                <Alert
                  icon={<ExclamationTriangleIcon style={{ width: rem(16) }} />}
                  title="Error"
                  color={error.includes("already exists") ? "blue" : "red"}
                  mb="md"
                >
                  {error}
                </Alert>
              ) : (
                <Stack gap="md">
                  <Grid>
                    <Grid.Col span={6}>
                      <TextInput
                        label="Search skills"
                        placeholder="Search skills..."
                        leftSection={
                          <MagnifyingGlassIcon style={{ width: rem(18) }} />
                        }
                        value={searchQuery}
                        onChange={handleSearchChange}
                        description={`${skills.length} skills added • ${(availableTechnologies?.length || 0) - skills.length
                          } technologies available to add`}
                      />
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Select
                        label="Show only category"
                        placeholder="All categories"
                        data={[{ value: "all", label: "All categories" }]}
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        clearable
                        description="Filter skills by technology category"
                      />
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Group gap="xs">
                        <Select
                          label="Order by"
                          placeholder="Skill or Technology"
                          data={[
                            { value: "technology", label: "Skill or Technology" },
                            { value: "skillLevel", label: "Skill Level" },
                            {
                              value: "yearsExperience",
                              label: "Years Experience",
                            },
                            { value: "status", label: "Status" },
                          ]}
                          value={sortBy}
                          onChange={(value) =>
                            setSortBy(value as typeof sortBy)
                          }
                          style={{ flex: 1 }}
                          description="Sort the skill list"
                        />
                        <ActionIcon
                          variant="subtle"
                          onClick={() =>
                            setSortDirection((prev) =>
                              prev === "asc" ? "desc" : "asc"
                            )
                          }
                          title={`Sort ${sortDirection === "asc" ? "descending" : "ascending"
                            }`}
                          style={{ marginTop: "25px" }}
                        >
                          {sortDirection === "asc" ? (
                            <ChevronUpIcon style={{ width: rem(18) }} />
                          ) : (
                            <ChevronDownIcon style={{ width: rem(18) }} />
                          )}
                        </ActionIcon>
                      </Group>
                    </Grid.Col>
                  </Grid>

                  {/* Add Skill Form */}
                  {showAddForm && (
                    <Card withBorder p="md">
                      <Stack gap="md">
                        <Group justify="space-between">
                          <Text fw={500} size="lg">
                            Add New Skill
                          </Text>
                          <ActionIcon
                            variant="subtle"
                            onClick={handleCancelAddSkill}
                          >
                            <XMarkIcon style={{ width: rem(18) }} />
                          </ActionIcon>
                        </Group>

                        <Grid>
                          <Grid.Col span={4}>
                            <Select
                              label="Skill or Technology"
                              placeholder="Select skill or technology"
                              data={(availableTechnologies || [])
                                .filter(
                                  (tech) =>
                                    !skills.some(
                                      (skill) => skill.technologyId === tech.id
                                    )
                                )
                                .map((tech) => ({
                                  value: tech.id.toString(),
                                  label: tech.name,
                                }))}
                              searchable
                              required
                              value={addFormTechnology}
                              onChange={setAddFormTechnology}
                              rightSection={
                                <Tooltip
                                  label="Includes technologies, frameworks, tools, soft skills, methodologies, and other skills (e.g. ML, Recruitment, AI, DevOps tools)."
                                  position="top"
                                  multiline
                                  maw={260}
                                  withArrow
                                >
                                  <ActionIcon variant="subtle" color="gray" size="sm" tabIndex={-1}>
                                    <InformationCircleIcon style={{ width: rem(16) }} />
                                  </ActionIcon>
                                </Tooltip>
                              }
                            />
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <Stack gap="xs">
                              <Text size="sm" fw={500}>
                                Skill Level (1-5)
                              </Text>
                              <Rating
                                value={addFormSkillLevel}
                                onChange={setAddFormSkillLevel}
                                size="md"
                                count={5}
                              />
                            </Stack>
                          </Grid.Col>
                          <Grid.Col span={4}>
                            <TextInput
                              label="Years of Experience"
                              placeholder="e.g. 2.5"
                              value={addFormYearsInput}
                              onChange={handleAddFormYearsChange}
                              type="number"
                              step="0.1"
                              min="0"
                            />
                          </Grid.Col>
                        </Grid>

                        <Textarea
                          label="Comments"
                          placeholder="Optional comments about your experience with this technology..."
                          value={addFormComments}
                          onChange={(event) =>
                            setAddFormComments(event.currentTarget.value)
                          }
                          rows={3}
                          maxLength={1000}
                        />

                        <Group justify="space-between">
                          <Group gap="xs">
                            <Text size="sm" fw={500}>
                              Mark as favorite
                            </Text>
                            <ActionIcon
                              color={addFormIsFavorite ? "red" : "gray"}
                              variant={addFormIsFavorite ? "filled" : "subtle"}
                              onClick={() =>
                                setAddFormIsFavorite(!addFormIsFavorite)
                              }
                              size="lg"
                            >
                              {addFormIsFavorite ? (
                                <HeartIconSolid style={{ width: rem(20) }} />
                              ) : (
                                <HeartIcon style={{ width: rem(20) }} />
                              )}
                            </ActionIcon>
                          </Group>

                          <Group gap="xs">
                            <Button
                              variant="default"
                              onClick={handleCancelAddSkill}
                            >
                              Cancel
                            </Button>
                            <Tooltip
                              label="Skill assignment is temporarily unavailable — this feature is being rebuilt."
                              disabled={skillAssignmentEnabled}
                            >
                              <Button
                                onClick={handleSaveNewSkill}
                                disabled={!addFormTechnology || isAddingSkill || !skillAssignmentEnabled}
                                loading={isAddingSkill}
                                leftSection={
                                  <CheckIcon style={{ width: rem(16) }} />
                                }
                              >
                                Add Skill
                              </Button>
                            </Tooltip>
                          </Group>
                        </Group>
                      </Stack>
                    </Card>
                  )}

                  {/* Similar Skills Section */}
                  {similarSkills.length > 0 && (
                    <Paper p="md" withBorder>
                      <Group mb="xs">
                        <Text fw={500} size="sm">
                          Similar Skills You Might Want to Add
                        </Text>
                        {isLoadingSimilarSkills && <Loader size="xs" />}
                      </Group>
                      <Group gap="xs">
                        {similarSkills.map((skill, index) => {
                          const tech = availableTechnologies?.find(
                            (t) => t.name.toLowerCase() === skill.toLowerCase()
                          );
                          const existingSkill = tech
                            ? skills.find((s) => s.technologyId === tech.id)
                            : null;
                          const isExisting = !!existingSkill;

                          return (
                            <Chip
                              key={index}
                              variant={isExisting ? "outline" : "filled"}
                              color={isExisting ? "gray" : "blue"}
                              size="sm"
                              radius="sm"
                              checked={false}
                              onClick={() => {
                                if (tech) {
                                  if (existingSkill) {
                                    setError(
                                      `Skill "${tech.name}" already exists. You can create a new assessment for it.`
                                    );
                                    setTimeout(() => setError(null), 3000);
                                  } else {
                                    setAddFormTechnology(tech.id.toString());
                                    setAddFormSkillLevel(1);
                                    setAddFormYearsExperience(null);
                                    setAddFormYearsInput("");
                                    setAddFormIsFavorite(false);
                                    setAddFormComments("");
                                    setShowAddForm(true);
                                  }
                                }
                              }}
                              styles={{
                                root: {
                                  cursor: "pointer",
                                  opacity: isExisting ? 0.7 : 1,
                                },
                                iconWrapper: {
                                  display: "none",
                                },
                              }}
                            >
                              {skill} {isExisting && "(Exists)"}
                            </Chip>
                          );
                        })}
                      </Group>
                    </Paper>
                  )}

                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => {
                            if (sortBy === "technology") {
                              setSortDirection((prev) =>
                                prev === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortBy("technology");
                              setSortDirection("asc");
                            }
                          }}
                        >
                          <Group gap="xs" justify="space-between">
                            <Text>Skill or Technology</Text>
                            {sortBy === "technology" &&
                              (sortDirection === "asc" ? (
                                <ChevronUpIcon style={{ width: rem(14) }} />
                              ) : (
                                <ChevronDownIcon style={{ width: rem(14) }} />
                              ))}
                          </Group>
                        </Table.Th>
                        <Table.Th
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => {
                            if (sortBy === "skillLevel") {
                              setSortDirection((prev) =>
                                prev === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortBy("skillLevel");
                              setSortDirection("asc");
                            }
                          }}
                        >
                          <Group gap="xs" justify="space-between">
                            <Text>Skill Level</Text>
                            {sortBy === "skillLevel" &&
                              (sortDirection === "asc" ? (
                                <ChevronUpIcon style={{ width: rem(14) }} />
                              ) : (
                                <ChevronDownIcon style={{ width: rem(14) }} />
                              ))}
                          </Group>
                        </Table.Th>
                        <Table.Th
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => {
                            if (sortBy === "yearsExperience") {
                              setSortDirection((prev) =>
                                prev === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortBy("yearsExperience");
                              setSortDirection("asc");
                            }
                          }}
                        >
                          <Group gap="xs" justify="space-between">
                            <Text>Years Experience</Text>
                            {sortBy === "yearsExperience" &&
                              (sortDirection === "asc" ? (
                                <ChevronUpIcon style={{ width: rem(14) }} />
                              ) : (
                                <ChevronDownIcon style={{ width: rem(14) }} />
                              ))}
                          </Group>
                        </Table.Th>
                        <Table.Th
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => {
                            if (sortBy === "status") {
                              setSortDirection((prev) =>
                                prev === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortBy("status");
                              setSortDirection("asc");
                            }
                          }}
                        >
                          <Group gap="xs" justify="space-between">
                            <Text>Status</Text>
                            {sortBy === "status" &&
                              (sortDirection === "asc" ? (
                                <ChevronUpIcon style={{ width: rem(14) }} />
                              ) : (
                                <ChevronDownIcon style={{ width: rem(14) }} />
                              ))}
                          </Group>
                        </Table.Th>
                        <Table.Th>Favorite</Table.Th>
                        <Table.Th>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filteredSkills.length === 0 ? (
                        <Table.Tr>
                          <Table.Td colSpan={6} align="center">
                            <Stack align="center" gap="md">
                              <Text c="dimmed">
                                No skills found. Add a new skill to get started.
                              </Text>
                              {noSkillsFound && (
                                <Button
                                  variant="outline"
                                  component="a"
                                  href={ROUTES.CV_EDITOR}
                                >
                                  Add more skills on My CV
                                </Button>
                              )}
                            </Stack>
                          </Table.Td>
                        </Table.Tr>
                      ) : (
                        filteredSkills.map((skill) => {
                          const hasChanges =
                            isBulkEditMode &&
                            bulkEditChanges.has(skill.personTechnologyId);
                          return (
                            <Table.Tr
                              key={skill.personTechnologyId}
                              style={{
                                backgroundColor: hasChanges
                                  ? "var(--mantine-color-blue-0)"
                                  : undefined,
                                borderLeft: hasChanges
                                  ? "3px solid var(--mantine-color-blue-6)"
                                  : undefined,
                              }}
                            >
                              <Table.Td>
                                <Text fw={500}>{skill.technologyName}</Text>
                                {skill.assessmentComments && (
                                  <Text size="xs" c="dimmed" lineClamp={1}>
                                    {skill.assessmentComments}
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                {isBulkEditMode && !skill.isConfirmed ? (
                                  <Rating
                                    value={skill.levelSelfRating || 0}
                                    onChange={(value) =>
                                      handleBulkSkillLevelChange(skill, value)
                                    }
                                    count={5}
                                    emptySymbol={
                                      <StarIcon style={{ width: rem(16) }} />
                                    }
                                  />
                                ) : skill.isConfirmed &&
                                  skill.levelManagerRating ? (
                                  <>
                                    <Rating
                                      value={skill.levelManagerRating}
                                      readOnly
                                      count={5}
                                      emptySymbol={
                                        <StarIcon style={{ width: rem(16) }} />
                                      }
                                    />
                                    {skill.levelSelfRating &&
                                      skill.levelSelfRating !==
                                      skill.levelManagerRating && (
                                        <Text size="xs" c="dimmed">
                                          Self-rating: {skill.levelSelfRating}/5
                                        </Text>
                                      )}
                                  </>
                                ) : (
                                  <Rating
                                    value={skill.levelSelfRating || 0}
                                    readOnly
                                    count={5}
                                    emptySymbol={
                                      <StarIcon style={{ width: rem(16) }} />
                                    }
                                  />
                                )}
                              </Table.Td>
                              <Table.Td>
                                {isBulkEditMode && !skill.isConfirmed ? (
                                  <TextInput
                                    value={getYearsInputValue(skill)}
                                    onChange={(event) => {
                                      const value = event.currentTarget.value;
                                      handleBulkYearsInputChange(skill, value);
                                    }}
                                    onBlur={(event) => {
                                      const value = event.currentTarget.value;
                                      if (value.trim() === "") {
                                        handleBulkYearsChange(skill, null);
                                      } else {
                                        const numValue = parseFloat(value);
                                        if (!isNaN(numValue)) {
                                          handleBulkYearsChange(
                                            skill,
                                            numValue
                                          );
                                        }
                                      }
                                    }}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter") {
                                        const value = event.currentTarget.value;
                                        if (value.trim() === "") {
                                          handleBulkYearsChange(skill, null);
                                        } else {
                                          const numValue = parseFloat(value);
                                          if (!isNaN(numValue)) {
                                            handleBulkYearsChange(
                                              skill,
                                              numValue
                                            );
                                          }
                                        }
                                        event.currentTarget.blur();
                                      }
                                    }}
                                    placeholder="Years"
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    size="xs"
                                    styles={{ input: { width: "80px" } }}
                                  />
                                ) : (
                                  <Text>
                                    {formatYearsOfExperience(
                                      getTechnologyExperience(skill.technologyName) ?? skill.yearsExperience
                                    )}
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                {getStatusBadge(skill)}
                                {skill.hasAssessment &&
                                  skill.assessmentDate && (
                                    <Text size="xs" c="dimmed">
                                      {new Date(
                                        skill.assessmentDate
                                      ).toLocaleDateString()}
                                    </Text>
                                  )}
                              </Table.Td>
                              <Table.Td>
                                <ActionIcon
                                  variant="subtle"
                                  onClick={() => handleToggleFavorite(skill)}
                                  style={{ cursor: "pointer" }}
                                >
                                  {skill.favourite ? (
                                    <HeartIconSolid
                                      style={{
                                        width: rem(18),
                                        color: "var(--mantine-color-red-6)",
                                      }}
                                    />
                                  ) : (
                                    <HeartIcon style={{ width: rem(18) }} />
                                  )}
                                </ActionIcon>
                              </Table.Td>
                              <Table.Td>
                                <Group gap="xs">
                                  <Tooltip
                                    label={
                                      skillAssignmentEnabled
                                        ? "Create new assessment"
                                        : "Skill assignment is temporarily unavailable — this feature is being rebuilt."
                                    }
                                  >
                                    <ActionIcon
                                      variant="subtle"
                                      onClick={() =>
                                        handleCreateAssessment(skill)
                                      }
                                      color="blue"
                                      disabled={!skillAssignmentEnabled}
                                    >
                                      <PencilSquareIcon
                                        style={{ width: rem(16) }}
                                      />
                                    </ActionIcon>
                                  </Tooltip>
                                  {skill.hasAssessment &&
                                    !skill.isConfirmed && (
                                      <Tooltip label="Pending manager confirmation">
                                        <ActionIcon
                                          variant="subtle"
                                          color="yellow"
                                        >
                                          <ClockIcon
                                            style={{ width: rem(16) }}
                                          />
                                        </ActionIcon>
                                      </Tooltip>
                                    )}
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })
                      )}
                    </Table.Tbody>
                  </Table>
                </Stack>
              )}
            </Paper>
          </Stack>
        </Container>

        {/* Assessment Modal */}
        <Modal
          opened={showAssessmentModal}
          onClose={handleCancelAssessment}
          title={`Create Assessment: ${assessmentSkill?.technologyName}`}
          size="md"
        >
          <Stack gap="md">
            <Alert
              icon={<InformationCircleIcon style={{ width: rem(16) }} />}
              title="Assessment Information"
              color="blue"
            >
              Creating an assessment will update your skill level (1-5 scale)
              and experience. This assessment will be sent to your manager for
              confirmation.
            </Alert>

            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Skill Level (Self-Rating 1-5)
              </Text>
              <Rating
                value={assessmentFormSkillLevel}
                onChange={setAssessmentFormSkillLevel}
                size="md"
                count={5}
              />
            </Stack>

            <TextInput
              label="Years of Experience"
              placeholder="e.g. 2.5"
              value={assessmentFormYearsInput}
              onChange={handleAssessmentFormYearsChange}
              type="number"
              step="0.1"
              min="0"
            />

            <Textarea
              label="Comments"
              placeholder="Describe your experience and skills with this technology..."
              value={assessmentFormComments}
              onChange={(event) =>
                setAssessmentFormComments(event.currentTarget.value)
              }
              rows={4}
              maxLength={1000}
            />

            <Group justify="flex-end" gap="xs">
              <Button variant="default" onClick={handleCancelAssessment}>
                Cancel
              </Button>
              <Tooltip
                label="Skill assignment is temporarily unavailable — this feature is being rebuilt."
                disabled={skillAssignmentEnabled}
              >
                <Button
                  onClick={handleSaveAssessment}
                  loading={isCreatingAssessment}
                  disabled={!skillAssignmentEnabled}
                  leftSection={<CheckIcon style={{ width: rem(16) }} />}
                >
                  Create Assessment
                </Button>
              </Tooltip>
            </Group>
          </Stack>
        </Modal>
      </AppShell.Main>
    </AppShell>
  );
}
